/** Where a model file fetch came from: the Download button, a tone zip, or the player. */
export type FetchKind = 'download' | 'zip' | 'player';

export type FileTiming = {
  /** Fetch start to the last body byte, including the redirect through our route. */
  duration_ms: number;
  /** Null when the browser hides the breakdown (files. without Timing-Allow-Origin). */
  ttfb_ms: number | null;
  bytes: number;
  /** Cloudflare data center from `cf-ray`; null when CORS doesn't expose it. */
  colo: string | null;
  cache: string | null;
};

export type ResourceBreakdown = {
  duration_ms: number;
  ttfb_ms: number | null;
  bytes: number | null;
};

const round = (ms: number) => Math.round(ms);

/** `"a54d78a82c048465-EWR"` → `"EWR"`. */
export function coloFromRay(ray: string | null): string | null {
  const colo = ray?.split('-').pop();
  return colo && /^[A-Z]{3}$/.test(colo) ? colo : null;
}

/** Cross-origin entries without Timing-Allow-Origin zero `responseStart` and the sizes. */
export function resourceBreakdown(e: {
  startTime: number;
  responseStart: number;
  responseEnd: number;
  encodedBodySize: number;
}): ResourceBreakdown {
  return {
    duration_ms: round(e.responseEnd - e.startTime),
    ttfb_ms: e.responseStart > 0 ? round(e.responseStart - e.startTime) : null,
    bytes: e.encodedBodySize > 0 ? e.encodedBodySize : null,
  };
}

const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const mostCommon = (xs: (string | null)[]) => {
  const counts = new Map<string, number>();
  for (const x of xs) if (x) counts.set(x, (counts.get(x) ?? 0) + 1);
  return Array.from(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
};

export type ZipTiming = {
  files: number;
  bytes: number;
  duration_ms: number;
  median_ms: number;
  slowest_ms: number;
  colo: string | null;
  cache_hits: number;
};

/** One event per zip: the wall time of the whole fetch plus per-file shape. Null for an empty zip. */
export function summarizeZip(files: FileTiming[], wallMs: number): ZipTiming | null {
  if (!files.length) return null;
  const durations = files.map((f) => f.duration_ms);
  return {
    files: files.length,
    bytes: files.reduce((n, f) => n + f.bytes, 0),
    duration_ms: round(wallMs),
    median_ms: round(median(durations)),
    slowest_ms: Math.max(...durations),
    colo: mostCommon(files.map((f) => f.colo)),
    cache_hits: files.filter((f) => f.cache === 'HIT').length,
  };
}

/** Our route paths, absolute or relative; files. URLs after the redirect don't match. */
export const isModelFileRoute = (url: string) => /\/api\/files\/models\/\d+\//.test(url);
